import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Users,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';

interface CalendarViewProps {
  onCreateEvent?: () => void;
}

export function CalendarView({ onCreateEvent }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [view, setView] = useState<'month' | 'week'>('month');

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = useMemo(
    () => eachDayOfInterval({ start: calendarStart, end: calendarEnd }),
    [calendarStart, calendarEnd]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['events', format(monthStart, 'yyyy-MM'), format(monthEnd, 'yyyy-MM')],
    queryFn: () => api.getEvents(monthStart.toISOString(), monthEnd.toISOString()),
  });

  const events = data?.data?.events || [];
  const syncError = data?.data?.syncError;

  const getEventsForDay = (date: Date) => {
    return events.filter((event) => isSameDay(new Date(event.startTime), date));
  };

  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
  };

  return (
    <div className="flex h-full">
      {/* Calendar Grid */}
      <div className="flex-1 p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">{format(currentDate, 'MMMM yyyy')}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigateMonth('prev')}
                className="rounded-lg p-2 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="rounded-lg px-3 py-1 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth('next')}
                className="rounded-lg p-2 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-border p-1">
              <button
                onClick={() => setView('month')}
                className={clsx(
                  'rounded-md px-3 py-1 text-sm',
                  view === 'month' ? 'bg-surface-hover text-text-primary' : 'text-text-tertiary'
                )}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={clsx(
                  'rounded-md px-3 py-1 text-sm',
                  view === 'week' ? 'bg-surface-hover text-text-primary' : 'text-text-tertiary'
                )}
              >
                Week
              </button>
            </div>
            {onCreateEvent && (
              <button onClick={onCreateEvent} className="btn-primary">
                <Plus className="h-4 w-4" />
                Event
              </button>
            )}
          </div>

          {syncError && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-warning-muted p-3 text-warning">
              <AlertCircle className="h-4 w-4" />
              <p className="text-xs font-medium">
                Note: Could not sync with Google Calendar ({syncError}). Showing cached events.
              </p>
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="rounded-xl border border-border bg-surface">
          {/* Days header */}
          <div className="grid grid-cols-7 border-b border-border">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="px-2 py-3 text-center text-xs font-medium text-text-tertiary"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const dayIsToday = isToday(day);

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={clsx(
                    'group relative min-h-[100px] border-b border-r border-border p-2 text-left transition-colors',
                    index % 7 === 6 && 'border-r-0',
                    index >= days.length - 7 && 'border-b-0',
                    !isCurrentMonth && 'bg-background',
                    isSelected && 'bg-accent-muted',
                    !isSelected && 'hover:bg-surface-hover'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-flex h-7 w-7 items-center justify-center rounded-full text-sm',
                      dayIsToday && 'bg-accent text-white',
                      !dayIsToday && !isCurrentMonth && 'text-text-tertiary',
                      !dayIsToday && isCurrentMonth && 'text-text-primary'
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.eventId}
                        className="truncate rounded bg-accent-muted px-1.5 py-0.5 text-2xs text-accent"
                      >
                        {format(new Date(event.startTime), 'HH:mm')} {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-2xs text-text-tertiary">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Event Details Sidebar */}
      <div className="w-80 border-l border-border bg-surface p-6">
        {selectedDate ? (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold">{format(selectedDate, 'EEEE')}</h3>
              <p className="text-sm text-text-tertiary">
                {format(selectedDate, 'MMMM d, yyyy')}
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : selectedDayEvents.length > 0 ? (
              <div className="space-y-4">
                {selectedDayEvents.map((event) => (
                  <div
                    key={event.eventId}
                    className="rounded-lg border border-border bg-background p-4"
                  >
                    <h4 className="font-medium">{event.title}</h4>
                    <div className="mt-2 space-y-2 text-sm text-text-tertiary">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>
                          {format(new Date(event.startTime), 'HH:mm')} -{' '}
                          {format(new Date(event.endTime), 'HH:mm')}
                        </span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.attendees && event.attendees.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>{event.attendees.length} attendees</span>
                        </div>
                      )}
                    </div>
                    {event.description && (
                      <p className="mt-3 text-sm text-text-secondary">{event.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-surface-hover">
                  <Clock className="h-6 w-6 text-text-tertiary" />
                </div>
                <p className="text-sm text-text-tertiary">No events scheduled</p>
                {onCreateEvent && (
                  <button
                    onClick={onCreateEvent}
                    className="mt-4 text-sm text-accent hover:underline"
                  >
                    Add an event
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-text-tertiary">
            Select a day to view events
          </div>
        )}
      </div>
    </div>
  );
}

